import { useToast } from "@reconvert/react-ui-component";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectAppState, setIFrameLink, updateBreadcrumbValue } from "../app/slice/appSlice";
import {
  createSlotAsync,
  deleteSlotAsync,
  selectSlotsState,
  setCreatingSlot,
  updateSlot,
  updateSlotAsync,
} from "../app/slice/slotSlice";
import { APP_PLATFORM, WIDGET_EDITOR_URL } from "../config";
import { getToken } from "../services/auth.service";
import { EmptySlotOption, Platform } from "../models";
import useConditions from "./useConditions";
import { generateBreadcrumbFormat, IFramePostMessageType, sendPostMessage } from "../utils/helpers";
import { setSelectedVariantId } from "../app/slice/conditionSlice";
import { LoggerContext } from "@reconvert/react-logger";
import { MAX_CUSTOM_SLOT, PreMadeTemplateType } from "@reconvert/reconvert-utils";
import i18n from "../utils/i18n";
import { UNLEASH_ENABLE_ANNOUNCEMENT_BAR } from "../utils/constant";
import { useFlag } from "@unleash/proxy-client-react";

export interface OpenPreMadeTemplateModalParams {
  slotNo?: number | null;
  slotType?: PreMadeTemplateType | null;
}

interface SlotReturnType {
  openDeleteSlotModal: boolean;
  deletingSlot: boolean;
  handleCreateSlot: (slotNo?: number | null, copyFromSlotNo?: number | null, copyFromVariantId?: string | null) => void;
  openPreMadeTemplateModal: (params?: OpenPreMadeTemplateModalParams) => boolean;
  handleSlotNameUpdate: (id: string, oldName: any, name: any) => void;
  setOpenDeleteSlotModal: (value: boolean) => void;
  handleSlotDelete: (slotNo: number) => void;
  handleSlotEmptyOptionUpdate: (id: string, oldValue: EmptySlotOption, emptySlotOption: EmptySlotOption) => void;
  isPreMadeTemplateSlot: (slotNumber: number, slotType?: PreMadeTemplateType) => boolean;
}

const useSlots = (): SlotReturnType => {
  const { t } = useTranslation();
  const { success, error } = useToast();
  const { selectedCondition } = useConditions();
  const { activePage, platformStoreId } = useAppSelector(selectAppState);
  const { slots, creatingSlot, isButtonDisabled } = useAppSelector(selectSlotsState);
  const isAnnouncementBarEnabled = useFlag(UNLEASH_ENABLE_ANNOUNCEMENT_BAR);
  const dispatch = useAppDispatch();
  const [openDeleteSlotModal, setOpenDeleteSlotModal] = useState(false);
  const [deletingSlot, setDeletingSlot] = useState(false);
  const { logger } = useContext(LoggerContext);

  const handleCreateSlot = useCallback(
    (slotNo?: number | null, copyFromSlotNo?: number | null, copyFromVariantId?: string | null) => {
      logger.info(`Start Creating slot :: ${platformStoreId}`, { slotNo, copyFromSlotNo, copyFromVariantId });

      dispatch(setCreatingSlot(true));
      return dispatch(createSlotAsync({ slotNo, copyFromSlotNo, copyFromVariantId }))
        .unwrap()
        .then(async res => {
          success(`${t("Slot created successfully.")}`);

          dispatch(
            updateBreadcrumbValue({
              breadCrumb: generateBreadcrumbFormat({
                page: t(res?.data?.location as string),
                condition: t(selectedCondition?.name as string),
                slot: t(res?.data?.name as string),
                variant: t(res?.data?.variants[0] && res.data.variants[0].name as string),
              }),
            }),
          );

          res?.data?.variants[0] && dispatch(setSelectedVariantId(res.data.variants[0].id));

          const tokenRes = await getToken(platformStoreId!);

          logger.info(`Slot created :: ${platformStoreId}`, { token: tokenRes.response?.token });
          if (tokenRes.response?.token) {
            dispatch(
              setIFrameLink(
                `${WIDGET_EDITOR_URL}?variant=${res.data.variants[0].id}${
                  activePage! ? `&type=${activePage!}` : ""
                }&platformStoreId=${platformStoreId}&token=${tokenRes.response.token}${APP_PLATFORM === Platform.Wix ? "&isNewVariant=true" : ""}&conditionId=${selectedCondition?.id}&slotId=${res?.data?.variants[0]?.slotId}&slotNo=${res?.data?.variants[0]?.slotNo}${APP_PLATFORM === Platform.Wix ? "&openSlotPreview=true" : ""}&language=${i18n.language}${APP_PLATFORM === Platform.Shopify ? "&showWidgetSelected=true" : ""}`,
              ),
            );
          }
        })
        .catch(() => {
          error(`${t("Error while creating slot.")}`);
          logger.error("Error while creating slot");
        });
    },
    [logger, platformStoreId, dispatch, success, t, selectedCondition?.name, activePage, error, i18n.language],
  );

  const handleSlotNameUpdate = useCallback(
    (id: string, oldName: any, name: any) => {
      dispatch(updateSlot({ id, name }));
      dispatch(updateSlotAsync({ id, body: { name } }))
        .unwrap()
        .catch(() => {
          error(t("Failed to change slot name."));
          dispatch(updateSlot({ id, name: oldName }));
        });
    },
    [dispatch, error, t],
  );

  const handleSlotEmptyOptionUpdate = useCallback(
    (id: string, oldValue: EmptySlotOption, emptySlotOption: EmptySlotOption) => {
      dispatch(updateSlot({ id, emptySlotOption }));
      dispatch(updateSlotAsync({ id, body: { emptySlotOption } }))
        .unwrap()
        .catch(() => {
          error(t("Failed to change empty option for slot."));
          dispatch(updateSlot({ id, emptySlotOption: oldValue }));
        });
    },
    [dispatch, error, t],
  );

  const handleSlotDelete = useCallback(
    async (slotNo: number) => {
      setDeletingSlot(true);
      await dispatch(deleteSlotAsync(slotNo))
        .unwrap()
        .then(res => {
          dispatch(
            updateBreadcrumbValue({
              breadCrumb: generateBreadcrumbFormat({ page: t(res?.data?.location as string), condition: t(selectedCondition?.name as string) }),
            }),
          );
          success(t("Slot deleted successfully."));
          setDeletingSlot(false);
          setOpenDeleteSlotModal(false);
        })
        .catch(() => {
          setDeletingSlot(false);
          error(t("Error while deleting slot."));
        });
    },
    [dispatch, t, success, error],
  );

  const isPreMadeTemplateSlot = useCallback(
    (slotNumber: number, slotType?: PreMadeTemplateType): boolean => {
      return (
        APP_PLATFORM === Platform.Shopify &&
        (slotNumber > MAX_CUSTOM_SLOT || slotType === PreMadeTemplateType.ANNOUNCEMENT_BAR)
      );
    },
    [APP_PLATFORM],
  );

  const openPreMadeTemplateModal = useCallback((params?: OpenPreMadeTemplateModalParams): boolean => {
    if (false) {
      sendPostMessage(IFramePostMessageType.OPEN_PRE_MADE_TEMPLATE_MODAL, {
        showPreMadeTemplateModal: true,
        slotNo: params?.slotNo ?? null,
        slotType: params?.slotType ?? null,
        activePage: activePage ?? null,
        creatingSlot: creatingSlot ?? false,
        isButtonDisabled: isButtonDisabled ?? false,
        isAnnouncementBarEnabled,
        slots: slots.map(slot => ({ slotNo: slot.slotNo, slotType: slot.type })),
      });

      return true;
    }

    return false;
  }, [activePage, creatingSlot, isButtonDisabled, isAnnouncementBarEnabled, slots]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.event === IFramePostMessageType.CREATE_SLOT_WITH_TEMPLATE) {
        handleCreateSlot(event.data.slotNo, event.data.copyFromSlotNo, event.data.copyFromVariantId)
          .then(() => sendPostMessage(IFramePostMessageType.CLOSE_PRE_MADE_TEMPLATE_MODAL))
          .catch(() => {});
      }
    };

    window.addEventListener("message", handleMessage);

    return () => window.removeEventListener("message", handleMessage);
  }, [handleCreateSlot]);

  return {
    deletingSlot,
    openDeleteSlotModal,
    handleCreateSlot,
    openPreMadeTemplateModal,
    handleSlotNameUpdate,
    setOpenDeleteSlotModal,
    handleSlotDelete,
    handleSlotEmptyOptionUpdate,
    isPreMadeTemplateSlot,
  };
};

export default useSlots;
